import { isEmpty, type Optional } from '@festina-life/core'
import type { Token } from 'antlr4'

import FPlusParser from '../lexer/ANTLR/FPlusParser'
import { tokenize } from './tokenizer'
import type { ConstructorArgument, ConstructorCall } from './types'

const filterNonWhitespace = (tokens: Token[]): Token[] =>
  tokens.filter((t) => t.type !== FPlusParser.WS)

const extractName = (nonWs: Token[]): string => {
  const nameToken = nonWs[0]
  return nameToken.text
}

const isEmptyConstructor = (nonWs: Token[]): boolean =>
  nonWs.length === 3 &&
  isIdentifier(nonWs[0]) &&
  nonWs[1].type === FPlusParser.LPAREN &&
  nonWs[2].type === FPlusParser.RPAREN

const isIdentifier = (token: Token): boolean =>
  token.type === FPlusParser.Identifier ||
  token.type === FPlusParser.IdentifierOrSoftKey

const isCapitalisedIdentifier = (token: Token): boolean =>
  isIdentifier(token) && token.text[0].toUpperCase() === token.text[0]

export function isValidConstructorTokens(code: string): boolean {
  const trimmed = code.trim()
  const tokens = tokenize(trimmed)

  const nonWhiteSpace = filterNonWhitespace(tokens)
  if (!nonWhiteSpace.length) {
    return false
  }
  const hasLeadingIdentifier = isCapitalisedIdentifier(nonWhiteSpace[0])
  const isSecondTokenLeftParenthesis =
    nonWhiteSpace[1]?.type === FPlusParser.LPAREN
  const isLastTokenRightParenthesis =
    nonWhiteSpace[nonWhiteSpace.length - 1]?.type === FPlusParser.RPAREN

  if (
    !hasLeadingIdentifier ||
    !isSecondTokenLeftParenthesis ||
    !isLastTokenRightParenthesis
  ) {
    return false
  }

  if (isEmptyConstructor(nonWhiteSpace)) {
    return true
  }

  // Validate that all parameter assignments are valid
  let depth = 0
  let areAssignmentsValid = false
  for (let i = 0; i < nonWhiteSpace.length; i += 1) {
    const token = nonWhiteSpace[i]
    if (token.type === FPlusParser.LPAREN) {
      depth += 1
    } else if (token.type === FPlusParser.RPAREN) {
      depth -= 1
    } else if (depth === 1 && token.type === FPlusParser.ASSIGNMENT) {
      const prev = nonWhiteSpace[i - 1]
      const next = nonWhiteSpace[i + 1]
      // Check that we have a valid identifier before the assignment
      if (!isIdentifier(prev)) {
        return false
      }
      // Check that we have a meaningful value after the assignment
      if (
        !next ||
        next.type === FPlusParser.COMMA ||
        next.type === FPlusParser.RPAREN ||
        next.type === FPlusParser.ASSIGNMENT
      ) {
        return false
      }
      // Found a valid assignment
      areAssignmentsValid = true
    }
  }
  return areAssignmentsValid
}

export const parseConstructorCall = (
  code: string,
): Optional<ConstructorCall> => {
  if (!isValidConstructorTokens(code)) {
    return undefined
  }

  const trimmed = code.trim()
  const tokens = tokenize(trimmed)

  const nonWhitespaceTokens = filterNonWhitespace(tokens)
  const name = extractName(nonWhitespaceTokens)
  if (isEmptyConstructor(nonWhitespaceTokens)) {
    return { name, arguments: [] }
  }

  const constructorArguments: ConstructorArgument[] = []
  let depth = 0

  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i]
    if (token.type === FPlusParser.WS) {
      continue
    }
    if (token.type === FPlusParser.LPAREN) {
      depth += 1
      continue
    }
    if (token.type === FPlusParser.RPAREN) {
      depth -= 1
      continue
    }

    if (depth === 1 && token.type === FPlusParser.ASSIGNMENT) {
      // Previous non-ws token must be identifier
      let j = i - 1
      while (j >= 0 && tokens[j].type === FPlusParser.WS) {
        j -= 1
      }
      const previousNonWhiteSpaceToken = j >= 0 ? tokens[j] : undefined
      if (
        !previousNonWhiteSpaceToken ||
        !isIdentifier(previousNonWhiteSpaceToken)
      ) {
        return undefined
      }
      const argumentName = previousNonWhiteSpaceToken.text

      // First value token after '='
      let k = i + 1
      while (k < tokens.length && tokens[k].type === FPlusParser.WS) {
        k += 1
      }
      if (k >= tokens.length) {
        return undefined
      }

      // Value spans tokens until depth returns to 1 and we hit COMMA or RPAREN
      let innerDepth = depth
      let innerBracketDepth = 0
      const valueStartToken = tokens[k]
      let valueEndToken: Token | undefined = undefined
      for (; k < tokens.length; k += 1) {
        const assignmentSideToken = tokens[k]
        if (assignmentSideToken.type === FPlusParser.WS) {
          continue
        }
        if (assignmentSideToken.type === FPlusParser.LPAREN) {
          innerDepth += 1
          // If we encounter a right parenthesis, decrease the innerDepth.
          // If innerDepth reaches 0, we've reached the end of the constructor call's argument list.
          // The value for this argument ends at the last non-whitespace token before this parenthesis.
        } else if (assignmentSideToken.type === FPlusParser.RPAREN) {
          innerDepth -= 1
          if (innerDepth === 0) {
            let back = k - 1
            while (back > i && tokens[back].type === FPlusParser.WS) {
              back -= 1
            }
            valueEndToken = tokens[back]
            break
          }
        } else if (assignmentSideToken.type === FPlusParser.LSQUARE) {
          innerBracketDepth += 1
        } else if (assignmentSideToken.type === FPlusParser.RSQUARE) {
          innerBracketDepth -= 1
        }
        // If we're at the top-level of the argument list (innerDepth === 1) and see a comma,
        // and we're not inside nested brackets, this marks the end of the current argument's value.
        // The value ends at the last non-whitespace token before the comma.
        if (
          innerDepth === 1 &&
          innerBracketDepth === 0 &&
          assignmentSideToken.type === FPlusParser.COMMA
        ) {
          let back = k - 1
          while (back > i && tokens[back].type === FPlusParser.WS) {
            back -= 1
          }
          valueEndToken = tokens[back]
          break
        }
      }

      if (!valueEndToken) {
        return undefined
      }

      const value = trimmed.substring(
        valueStartToken.start,
        valueEndToken.stop + 1,
      )
      if (value.trim() === '') {
        return undefined
      }

      constructorArguments.push({ name: argumentName, value })
    }
  }

  return { name, arguments: constructorArguments }
}

const extractElement = (
  tokens: Token[],
  content: string,
  startIndex: number,
  endIndex: number,
): Optional<string> => {
  if (startIndex >= tokens.length) {
    return undefined
  }

  const firstToken = tokens[startIndex]
  let lastTokenIndex = endIndex
  // Skip trailing whitespace
  while (
    lastTokenIndex >= startIndex &&
    tokens[lastTokenIndex].type === FPlusParser.WS
  ) {
    lastTokenIndex -= 1
  }

  if (lastTokenIndex >= startIndex) {
    const lastToken = tokens[lastTokenIndex]
    return content.substring(firstToken.start, lastToken.stop + 1)
  }

  return undefined
}

/**
 * Parses an array of constructor calls from a string representation.
 * Returns an array of ConstructorCall objects if all elements are valid constructor calls,
 * or undefined if the input is not a valid array or contains any non-constructor expressions.
 *
 * @param code - String representation of an array of constructor calls, e.g. "[Constructor1(key1 = "Foo"), Constructor2(key2 = "Bar")]"
 * @returns Array of ConstructorCall objects or undefined if parsing fails
 */
export const parseConstructorCallArray = (
  code: string,
): Optional<ConstructorCall[]> => {
  const trimmed = code.trim()

  // Check if the code is wrapped in brackets
  if (!trimmed.startsWith('[') || !trimmed.endsWith(']')) {
    return undefined
  }

  // Extract content between brackets
  const content = trimmed.substring(1, trimmed.length - 1).trim()

  // Handle empty array
  if (content === '') {
    return []
  }

  // Tokenize to properly split by top-level commas
  const tokens = tokenize(content)
  const elements: string[] = []
  let currentElementStartIndex = 0
  let depth = 0
  let bracketDepth = 0

  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i]

    if (token.type === FPlusParser.LPAREN) {
      depth += 1
    } else if (token.type === FPlusParser.RPAREN) {
      depth -= 1
    } else if (token.type === FPlusParser.LSQUARE) {
      bracketDepth += 1
    } else if (token.type === FPlusParser.RSQUARE) {
      bracketDepth -= 1
    } else if (
      token.type === FPlusParser.COMMA &&
      depth === 0 &&
      bracketDepth === 0
    ) {
      // Found a top-level comma - extract the element
      const elementText = extractElement(
        tokens,
        content,
        currentElementStartIndex,
        i - 1,
      )
      if (elementText) {
        elements.push(elementText)
      }
      currentElementStartIndex = i + 1
    }
  }

  // Add the last element
  const lastElementText = extractElement(
    tokens,
    content,
    currentElementStartIndex,
    tokens.length - 1,
  )
  if (lastElementText) {
    elements.push(lastElementText)
  }

  // Parse each element as a constructor call
  const constructorCalls: ConstructorCall[] = []
  for (const element of elements) {
    const trimmedElement = element.trim()
    if (trimmedElement === '') {
      continue
    }

    const constructorCall = parseConstructorCall(trimmedElement)
    if (isEmpty(constructorCall)) {
      // If any element is not a valid constructor call, return undefined
      return undefined
    }

    constructorCalls.push(constructorCall)
  }

  return constructorCalls
}
